import { timelineMinutesToClock, toTimelineMinutes } from "./resizeAssignmentTime";

/**
 * Grid + boundary snapping for the schedule creator.
 *
 * The timeline snaps to a 15-minute grid, which is fine in the middle of a set
 * but makes off-grid set boundaries unreachable: an artist that plays 20:05 →
 * 21:10 could never be filmed from its real start/end because the nearest grid
 * lines are 20:00/20:15 and 21:00/21:15. These helpers add the set's own
 * start/end as extra snap targets so the producer can land exactly on them,
 * while every other position still snaps to the quarter-hour grid.
 */

export const SNAP_STEP_MINUTES = 15;

/**
 * Width of one hour on the schedule creator's horizontal timeline at zoom 1.
 * Single source of truth: the grid, the block positions and the current-time
 * marker all scale from this, so they can never disagree about where an hour
 * sits.
 */
export const HOUR_WIDTH_PX = 240;

/**
 * Raw (UN-snapped) timeline minutes for a horizontal pixel offset. 60px = one
 * 15-minute slot at zoom 1, mirroring CrewSchedule's timeToPosition.
 */
export function positionToTimelineMinutes(
  position: number,
  startHour: number,
  zoom: number,
): number {
  const pxPerMinute = HOUR_WIDTH_PX / 60;
  return startHour * 60 + position / (pxPerMinute * zoom);
}

/**
 * Snap raw timeline minutes to a 15-min grid line OR one of the given snap
 * points (e.g. the artist's true set start/end), whichever is closest.
 *
 * `mode` controls how the grid line is chosen:
 *  - "round" (default): nearest grid line — right for dragging an edge to a
 *    precise position (resize).
 *  - "floor": the grid line at/just before the position — right for *placing*
 *    a block, so dropping anywhere inside a 15-min cell keeps its start at the
 *    cell's beginning instead of jumping to the next quarter.
 * Off-grid snap points stay reachable in both modes (they're exact targets,
 * compared by absolute distance).
 */
export function snapTimelineMinutes(
  rawMinutes: number,
  opts: { step?: number; snapPoints?: number[]; mode?: "round" | "floor" } = {},
): number {
  const step = opts.step ?? SNAP_STEP_MINUTES;
  const gridSnap =
    opts.mode === "floor"
      ? Math.floor(rawMinutes / step) * step
      : Math.round(rawMinutes / step) * step;

  let best = gridSnap;
  let bestDist = Math.abs(rawMinutes - gridSnap);
  for (const point of opts.snapPoints ?? []) {
    const dist = Math.abs(rawMinutes - point);
    if (dist < bestDist) {
      best = point;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Convert a pixel offset to a snapped "HH:MM" clock string, snapping to the
 * 15-min grid OR to any of the given boundary times (whichever is closest).
 * The boundary times are the real set edges, which may be off-grid.
 */
export function positionToSnappedTimeMulti(
  position: number,
  startHour: number,
  zoom: number,
  boundaryTimes: string[],
  mode: "round" | "floor" = "round",
): string {
  const raw = positionToTimelineMinutes(position, startHour, zoom);
  const snapPoints = boundaryTimes.map((t) => toTimelineMinutes(t, startHour));
  return timelineMinutesToClock(snapTimelineMinutes(raw, { snapPoints, mode }));
}

/**
 * Convert a pixel offset to a snapped "HH:MM" clock string. When `bounds` is
 * given the set's real start/end become reachable snap targets even if they
 * fall off the 15-min grid. `mode` is "round" for resizing an edge and "floor"
 * for placing a block (see snapTimelineMinutes).
 */
export function positionToSnappedTime(
  position: number,
  startHour: number,
  zoom: number,
  bounds?: { setStart: string; setEnd: string } | null,
  mode: "round" | "floor" = "round",
): string {
  return positionToSnappedTimeMulti(
    position,
    startHour,
    zoom,
    bounds ? [bounds.setStart, bounds.setEnd] : [],
    mode,
  );
}

/**
 * Fit a block of `durationMinutes` starting at `startTime` inside the set
 * [setStart, setEnd]: clamp the start into the set and, when the set is shorter
 * than the block, shorten the block to the set length. Used when moving a block
 * onto an artist so a 15-min block dropped on a 10-min set becomes 10 min
 * instead of spilling past the set end.
 */
export function fitBlockIntoSet(
  startTime: string,
  durationMinutes: number,
  setStart: string,
  setEnd: string,
  fallbackHour = 0,
): { start: string; end: string } {
  const ss = toTimelineMinutes(setStart, fallbackHour);
  const se = toTimelineMinutes(setEnd, fallbackHour);
  const setLength = Math.max(0, se - ss);
  const duration = Math.min(Math.max(0, durationMinutes), setLength);
  const start = Math.max(ss, Math.min(toTimelineMinutes(startTime, fallbackHour), se - duration));
  return {
    start: timelineMinutesToClock(start),
    end: timelineMinutesToClock(start + duration),
  };
}

/**
 * Duration of a block in minutes on the continuous timeline axis. Unlike a raw
 * clock subtraction, this stays positive for a block that crosses midnight
 * (e.g. 23:30 → 00:30 = 60 min, not −1380), so relocating such a block doesn't
 * collapse it to zero length.
 */
export function clockDurationMinutes(start: string, end: string, fallbackHour = 0): number {
  return toTimelineMinutes(end, fallbackHour) - toTimelineMinutes(start, fallbackHour);
}

/** Add `lengthMinutes` to a clock time, clamped so it never exceeds `maxEnd`. */
export function clampedEndTime(
  start: string,
  lengthMinutes: number,
  maxEnd: string | null,
  fallbackHour = 0,
): string {
  const startMin = toTimelineMinutes(start, fallbackHour);
  let endMin = startMin + lengthMinutes;
  if (maxEnd) {
    const maxMin = toTimelineMinutes(maxEnd, fallbackHour);
    if (maxMin > startMin) endMin = Math.min(endMin, maxMin);
  }
  return timelineMinutesToClock(endMin);
}
